import type { NoteColor } from "@sebas/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addMilliseconds } from "date-fns";
import { RRule, rrulestr } from "rrule";
import { api } from "./api";

export type Calendar = {
  id: string; name: string; color: NoteColor; teamId: string | null; isDefault: boolean;
};

export type EventRow = {
  id: string;
  calendarId: string;
  noteId: string | null;
  title: string;
  description: string | null;
  location: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
  rrule: string | null;
  color: NoteColor;
};

/** One concrete occurrence. Recurring masters expand into many of these. */
export type Occurrence = EventRow & {
  /** Stable per-occurrence key: the master id plus this instance's start. */
  occurrenceKey: string;
  start: Date;
  end: Date;
  isRecurring: boolean;
  /**
   * Set on events mirrored in from a connected external calendar. Those are
   * read-only here — they cannot be dragged, edited or deleted, only opened
   * where they actually live.
   */
  source?: "google";
  externalLink?: string | null;
};

/**
 * Expands recurring masters into occurrences inside [from, to].
 *
 * Duration is carried from the master rather than recomputed, so a 90-minute
 * meeting stays 90 minutes on every instance even across a DST boundary.
 */
export function expandEvents(rows: EventRow[], from: Date, to: Date): Occurrence[] {
  const out: Occurrence[] = [];

  for (const row of rows) {
    const start = new Date(row.startsAt);
    const end = new Date(row.endsAt);
    const durationMs = Math.max(0, end.getTime() - start.getTime());

    if (!row.rrule) {
      if (end >= from && start <= to) {
        out.push({ ...row, occurrenceKey: row.id, start, end, isRecurring: false });
      }
      continue;
    }

    try {
      const rule = rrulestr(row.rrule.startsWith("RRULE:") ? row.rrule : `RRULE:${row.rrule}`, {
        dtstart: start,
      });
      // `true` includes occurrences exactly on the boundaries.
      for (const occStart of rule.between(from, to, true)) {
        out.push({
          ...row,
          occurrenceKey: `${row.id}:${occStart.toISOString()}`,
          start: occStart,
          end: addMilliseconds(occStart, durationMs),
          isRecurring: true,
        });
      }
    } catch {
      // A malformed RRULE should show the master, not vanish the event entirely.
      if (end >= from && start <= to) {
        out.push({ ...row, occurrenceKey: row.id, start, end, isRecurring: false });
      }
    }
  }

  return out.sort((a, b) => a.start.getTime() - b.start.getTime());
}

export const RECURRENCE_PRESETS = [
  { label: "Does not repeat", value: "" },
  { label: "Every day", value: RRule.optionsToString({ freq: RRule.DAILY }).replace("RRULE:", "") },
  { label: "Every week", value: RRule.optionsToString({ freq: RRule.WEEKLY }).replace("RRULE:", "") },
  { label: "Every month", value: RRule.optionsToString({ freq: RRule.MONTHLY }).replace("RRULE:", "") },
  { label: "Every weekday", value: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR" },
] as const;

export function describeRecurrence(rrule: string | null): string | null {
  if (!rrule) return null;
  try {
    return rrulestr(rrule.startsWith("RRULE:") ? rrule : `RRULE:${rrule}`).toText();
  } catch {
    return "repeats";
  }
}

export function useCalendars() {
  return useQuery({ queryKey: ["calendars"], queryFn: () => api<{ calendars: Calendar[] }>("/v1/calendars") });
}

export function useEvents(from: Date, to: Date) {
  return useQuery({
    queryKey: ["events", from.toISOString(), to.toISOString()],
    queryFn: () =>
      api<{ events: EventRow[] }>(
        `/v1/events?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`,
      ),
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      api<{ event: EventRow }>("/v1/events", { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & Record<string, unknown>) =>
      api<{ event: EventRow }>(`/v1/events/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/events/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}
